import { useMemo } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDialogs } from '@toolpad/core/useDialogs';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

import {
  cancelPeriodEnrollmentMutation,
  getMyLearningCoursesOptions,
  getStudentScheduleQueryKey,
} from '@drivingdoc/api-client/generated/react-query';

import FeedbackDialog from 'components/Dialogs/FeedbackDialog';

import useAuthContext from 'hooks/useAuthContext';
import useStudentContext from 'hooks/useStudentContext';

import StudentPage from '../components/StudentPage';
import OnlineCourseProgressCard from './components/OnlineCourseProgressCard/OnlineCourseProgressCard';
import ProgressCard from './components/ProgressCard/ProgressCard';
import SectionHeader from './components/SectionHeader';
import StudentPeriod from './components/StudentPeriod/StudentPeriod';

const FlatAccordion = styled(Accordion)(() => ({
  backgroundColor: 'transparent',
  boxShadow: 'none',
  '&::before': {
    display: 'none',
  },
}));

const FlatAccordionSummary = styled(AccordionSummary)(() => ({
  padding: 0,
  minHeight: 'auto',

  '& .MuiAccordionSummary-content': {
    margin: 0,
  },
}));

const FlatAccordionDetails = styled(AccordionDetails)(() => ({
  padding: 0,
  marginTop: 8, // same as spacing={2} in Stack
}));

function StudentDashboard(): JSX.Element {
  // get the student id from the auth context
  const { userId: studentId } = useAuthContext();

  const dialogs = useDialogs();
  const queryClient = useQueryClient();
  const { student, loadingStudent, courseEnrollments, periodEnrollments, loadingPeriodEnrollments } =
    useStudentContext();

  // Check if student is enrolled in any online courses
  const onlineCourseIds = courseEnrollments
    .filter((enrollment) => enrollment.course.course_type === 'fully_online')
    .map((enrollment) => enrollment.course.id);
  const activeOnlineCourseIds = courseEnrollments
    .filter((enrollment) => enrollment.course.course_type === 'fully_online' && !enrollment.completed_at)
    .map((enrollment) => enrollment.course.id);
  const learningCourses = useQuery(getMyLearningCoursesOptions());
  const hasActiveOnlineCourseSummary = learningCourses.data?.some((course) =>
    activeOnlineCourseIds.includes(course.courseId),
  );

  // Organize periods into categories
  const organizedPeriods = useMemo(() => {
    const now = dayjs();

    const upcoming = periodEnrollments
      .filter((enrollment) => !enrollment.canceled_at && dayjs(enrollment.course_period.start).isAfter(now))
      .sort((a, b) => dayjs(a.course_period.start).diff(dayjs(b.course_period.start)));

    const past = periodEnrollments
      .filter((enrollment) => !enrollment.canceled_at && dayjs(enrollment.course_period.start).isBefore(now))
      .sort((a, b) => dayjs(b.course_period.start).diff(dayjs(a.course_period.start)));

    const canceled = periodEnrollments
      .filter((enrollment) => enrollment.canceled_at)
      .sort((a, b) => dayjs(b.canceled_at).diff(dayjs(a.canceled_at)));

    return { upcoming, past, canceled };
  }, [periodEnrollments]);

  const cancelPeriodEnrollment = useMutation({
    ...cancelPeriodEnrollmentMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getStudentScheduleQueryKey({ path: { id: studentId ?? '' } }),
        exact: false,
      });
      dialogs.open(FeedbackDialog, {
        icon: 'success',
        title: 'Course period has been canceled.',
      });
    },
    onError(error) {
      dialogs.open(FeedbackDialog, {
        title: 'Error',
        message: error.message,
      });
    },
  });

  dayjs.extend(advancedFormat);

  const cancel = async (id: string) => {
    await cancelPeriodEnrollment.mutateAsync({
      body: {
        coursePeriodId: id,
        studentId: studentId ?? '',
        cancel: true,
      },
    });
  };

  return (
    <StudentPage
      banner={<ProgressCard />}
      title={loadingStudent ? 'Welcome' : `Welcome ${student?.nickname ?? student?.first_name}`}
    >
      <OnlineCourseProgressCard courses={learningCourses} courseIds={onlineCourseIds} />

      {activeOnlineCourseIds.length > 0 && learningCourses.isSuccess && !hasActiveOnlineCourseSummary && (
        <Alert severity="warning" sx={{ mb: 4 }}>
          Your online course enrollment is active, but its course content is not available yet.
        </Alert>
      )}

      <Box
        display={'flex'}
        flexDirection={{ xs: 'column', sm: 'row' }}
        flex={'1 1 100%'}
        justifyContent={{ sm: 'space-between' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        marginBottom={4}
      >
        <Box marginBottom={{ xs: 1, sm: 0 }}>
          <Box marginBottom={2}>
            <Typography variant={'h6'} fontWeight={700}>
              Your Course Periods
            </Typography>
          </Box>
        </Box>
      </Box>

      {loadingPeriodEnrollments && (
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      )}

      {!loadingPeriodEnrollments && periodEnrollments?.length === 0 && (
        <Typography align="center">You are not enrolled in any course periods.</Typography>
      )}

      {!loadingPeriodEnrollments && periodEnrollments?.length > 0 && (
        <Stack direction="column" spacing={4}>
          {/* Upcoming Periods - Always Visible */}
          <Box>
            <SectionHeader title="Upcoming" count={organizedPeriods.upcoming.length} />

            {organizedPeriods.upcoming.length === 0 ? (
              <Typography color="text.secondary" align="center">
                You don&apos;t have any upcoming course periods scheduled.
              </Typography>
            ) : (
              <Stack direction="column" spacing={2}>
                {organizedPeriods.upcoming.map((enrollment) => (
                  <StudentPeriod
                    key={enrollment?.course_period?.id}
                    id={enrollment?.course_period?.id}
                    start={enrollment?.course_period?.start}
                    end={enrollment?.course_period?.end}
                    showDate={true}
                    courseTitle={enrollment?.course_period?.course_section?.course?.title ?? undefined}
                    sectionTitle={enrollment?.course_period?.course_section?.title}
                    attendance={enrollment?.attendance}
                    canceledAt={enrollment?.canceled_at}
                    allowCancellation={enrollment?.course_enrollment?.course_policy?.allow_cancellations ?? false}
                    lateCancellationDeadline={enrollment?.course_enrollment?.course_policy?.late_cancellation_deadline}
                    instructors={enrollment?.course_period?.instructors?.map(
                      (instructor) => instructor?.formal_name ?? '',
                    )}
                    location={enrollment?.course_period?.course_section?.tenant_location?.name}
                    onClickCancel={(id: string) => {
                      const policy = enrollment?.course_enrollment?.course_policy;
                      const lateCancelDeadline = dayjs(enrollment?.course_period?.start).subtract(
                        dayjs.duration(policy?.late_cancellation_deadline ?? {}),
                      );

                      let feeMessage: JSX.Element | undefined;

                      if (
                        policy?.late_cancellation_deadline &&
                        policy?.late_cancellation_fee &&
                        policy?.late_cancellation_fee > 0 &&
                        dayjs().isAfter(lateCancelDeadline)
                      ) {
                        feeMessage = (
                          <Typography variant="h6" align="center">
                            This will result in a late cancellation fee of{' '}
                            <b>${(policy.late_cancellation_fee / 100).toFixed(2)}</b>
                          </Typography>
                        );
                      } else if (policy?.cancellation_fee && policy.cancellation_fee > 0) {
                        feeMessage = (
                          <Typography variant="h6" align="center">
                            This will result in a cancellation fee of{' '}
                            <b>${(policy.cancellation_fee / 100).toFixed(2)}</b>
                          </Typography>
                        );
                      }

                      dialogs
                        .confirm(feeMessage ?? 'Are you sure you want to cancel this period?', {
                          title: 'Are you sure you want to cancel this period?',
                          okText: 'Yes',
                          cancelText: 'No',
                          severity: 'error',
                        })
                        .then((confirmed) => {
                          if (confirmed) {
                            cancel(id);
                          }
                        });
                    }}
                  />
                ))}
              </Stack>
            )}
          </Box>

          {/* Past Periods - Collapsible */}
          {organizedPeriods.past.length > 0 && (
            <FlatAccordion defaultExpanded={false}>
              <FlatAccordionSummary expandIcon={<ExpandMoreIcon />}>
                <SectionHeader title="Past" count={organizedPeriods.past.length} />
              </FlatAccordionSummary>
              <FlatAccordionDetails>
                <Stack direction="column" spacing={2}>
                  {organizedPeriods.past.map((enrollment) => (
                    <StudentPeriod
                      key={enrollment?.course_period?.id}
                      id={enrollment?.course_period?.id}
                      start={enrollment?.course_period?.start}
                      end={enrollment?.course_period?.end}
                      showDate={true}
                      courseTitle={enrollment?.course_period?.course_section?.course?.title ?? undefined}
                      sectionTitle={enrollment?.course_period?.course_section?.title}
                      attendance={enrollment?.attendance}
                      canceledAt={enrollment?.canceled_at}
                      allowCancellation={enrollment?.course_enrollment?.course_policy?.allow_cancellations ?? false}
                      lateCancellationDeadline={
                        enrollment?.course_enrollment?.course_policy?.late_cancellation_deadline
                      }
                      instructors={enrollment?.course_period?.instructors?.map(
                        (instructor) => instructor?.formal_name ?? '',
                      )}
                      location={enrollment?.course_period?.course_section?.tenant_location?.name}
                      onClickCancel={(id: string) => cancel(id)}
                    />
                  ))}
                </Stack>
              </FlatAccordionDetails>
            </FlatAccordion>
          )}

          {/* Canceled Periods - Collapsible */}
          {organizedPeriods.canceled.length > 0 && (
            <FlatAccordion defaultExpanded={false}>
              <FlatAccordionSummary expandIcon={<ExpandMoreIcon />}>
                <SectionHeader title="Canceled" color="error" count={organizedPeriods.canceled.length} />
              </FlatAccordionSummary>
              <FlatAccordionDetails>
                <Stack direction="column" spacing={2}>
                  {organizedPeriods.canceled.map((enrollment) => (
                    <StudentPeriod
                      key={enrollment?.course_period?.id}
                      id={enrollment?.course_period?.id}
                      start={enrollment?.course_period?.start}
                      end={enrollment?.course_period?.end}
                      showDate={true}
                      courseTitle={enrollment?.course_period?.course_section?.course?.title ?? undefined}
                      sectionTitle={enrollment?.course_period?.course_section?.title}
                      attendance={enrollment?.attendance}
                      canceledAt={enrollment?.canceled_at}
                      allowCancellation={enrollment?.course_enrollment?.course_policy?.allow_cancellations ?? false}
                      lateCancellationDeadline={
                        enrollment?.course_enrollment?.course_policy?.late_cancellation_deadline
                      }
                      instructors={enrollment?.course_period?.instructors?.map(
                        (instructor) => instructor?.formal_name ?? '',
                      )}
                      location={enrollment?.course_period?.course_section?.tenant_location?.name}
                      onClickCancel={(id: string) => cancel(id)}
                    />
                  ))}
                </Stack>
              </FlatAccordionDetails>
            </FlatAccordion>
          )}
        </Stack>
      )}

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={cancelPeriodEnrollment?.isPending}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </StudentPage>
  );
}

export default StudentDashboard;
